import { defineStore } from 'pinia'
import { apiClient } from '@/helpers/api'
import { handleError } from '@/services/errorHandler'
import { useUserStore } from '@/stores/UserStore'

export const useBillingStore = defineStore('BillingStore', {
  state: () => {
    return {
      coupons: [],

      buyMoreScansSuccess: false,
      buyMoreScansError: false,

      paymentMethods: [],

      createPaymentMethodLoading: false,

      paymentError: {},
      createPaymentError: {},
      addonsPaymentError: {},

      updateSubscriptionSuccess: false,
      updateSubscriptionError: false,

      cancelSubscriptionSuccess: false,

      loadingActivationSubscription: false,
      activationSubscriptionSuccess: false,
      activationSubscriptionError: false,

      loadingInvoices: false,
      invoices: [],
    }
  },

  getters: {
    invoicesList() {
      return this.invoices ? this.invoices.filter(item => item.total > 0) : []
    },
  },

  actions: {
    async fetchCoupons() {
      try {
        const { data } = await apiClient().get('users/me/coupons')
        this.coupons = data
      } catch (error) {
        console.error('fetchCoupons error', error)
        
      }
    },

    async buyMoreScans(body) {
      try {
        const { data } = await apiClient().post(`/add_ons`, body)

        if (data?.ok) {
          this.buyMoreScansSuccess = data
        } else {
          this.buyMoreScansError = data
        }
      } catch (error) {
        console.error('buyMoreScans error', error)
        this.buyMoreScansError = { ok: false }
        handleError(error, {
          category: 'api',
          action: 'buy-more-scans',
          component: 'BillingStore',
        })
      }
    },

    async deletePaymentMethod(stripe_payment_method_id) {
      try {
        await apiClient().delete(`/users/me/payment-methods/${stripe_payment_method_id}`)

        this.paymentMethods = this.paymentMethods.filter(item => item.id !== stripe_payment_method_id)
      } catch (error) {
        console.error('deletePaymentMethod error', error)
        handleError(error, {
          category: 'api',
          action: 'delete-payment-method',
          component: 'BillingStore',
          data: { stripe_payment_method_id },
        })
      }
    },

    async setDefaultPaymentMethod(stripe_payment_method_id) {
      try {
        const { data } = await apiClient().post(`/users/me/payment-methods/${stripe_payment_method_id}/set-default`)
      } catch (error) {
        console.error('setDefaultPaymentMethod error', error)
        handleError(error, {
          category: 'api',
          action: 'set-default-payment-method',
          component: 'BillingStore',
          data: { stripe_payment_method_id },
        })
      }
    },

    async createPaymentMethod(body, errorField = 'paymentError') {
      try {
        this.createPaymentMethodLoading = true
        const { response, data } = await apiClient().post(`/users/me/payment-methods`, body)

        if (response) {
          this[errorField] = response.data
        } else {
          this[errorField] = data
        }

        this.createPaymentMethodLoading = false
      } catch (error) {
        this.createPaymentMethodLoading = false
        console.error('createPaymentMethod error', error)
        handleError(error, {
          category: 'api',
          action: 'create-payment-method',
          component: 'BillingStore',
        })
      }
    },

    async fetchPaymentMethod() {
      try {
        const { data } = await apiClient().get(`/users/me/payment-methods`)

        if (!data?.ok) {
          throw Error(data)
        } else {
          this.paymentMethods = data.data
        }
      } catch (error) {
        console.error('fetchPaymentMethod error', error)
        handleError(error, {
          category: 'api',
          action: 'fetch-payment-method',
          component: 'BillingStore',
        })
      }
    },

    async updateSubscription(body) {
      this.updateSubscriptionSuccess = false
      this.updateSubscriptionError = false
      try {
        const { data, response } = await apiClient().post(`/users/me/subscription/update-plan`, body)

        if (!data || !data.ok || response) {
          this.updateSubscriptionError = true
          this.paymentError = data || response
        } else {
          this.updateSubscriptionSuccess = true
          this.paymentError = {}
        }

      } catch (error) {
        console.error('updateSubscription error', error)
        handleError(error, {
          category: 'api',
          action: 'update-subscription',
          component: 'BillingStore',
        })
        this.updateSubscriptionError = true
        this.paymentError = error
      }
    },

    async cancelSubscription(body) {
      try {
        const { data } = await apiClient().post(`/users/me/subscription/cancel`, body)
        this.cancelSubscriptionSuccess = true
      } catch (error) {
        console.error('cancelSubscription error', error)
        handleError(error, {
          category: 'api',
          action: 'cancel-subscription',
          component: 'BillingStore',
        })
      }
    },

    async activeSubscription(body) {
      const userStore = useUserStore()

      try {
        this.loadingActivationSubscription = true
        const { data, response } = await apiClient().post(`/users/${userStore.user.id}/subscription/activate`, body)

        if (response || (data && !data?.ok)) {
          this.paymentError = data || response.data
          throw Error(data)
        } else {
          this.paymentError = data
 
          this.activationSubscriptionSuccess = true

          await userStore.fetchUser()

          this.loadingActivationSubscription = false

          return this.activationSubscriptionSuccess
        }
      } catch (error) {
        console.error('activeSubscription error', error)
        this.loadingActivationSubscription = false
        this.activationSubscriptionError = true
        handleError(error, {
          category: 'api',
          action: 'activate-subscription',
          component: 'BillingStore',
        })
      }
    },


    async fetchInvoices(id, type) {
      try {
        this.loadingInvoices = true
        const { data, response } = await apiClient().get(`/users/me/invoices`)
        
        if (!data || !data?.ok) {
          throw new Error(response?.data?.message)
        } else {
          this.invoices = data.data
        }

        this.loadingInvoices = false
      } catch (error) {
        this.loadingInvoices = false
        console.error('fetchInvoices error', error)
        handleError(error, {
          category: 'api',
          action: 'fetch-invoices',
          component: 'BillingStore',
        })
      }
    },
  }
})